/**
 * The other products, named once.
 *
 * ## THIS FILE IS THE KIT. Do not edit the copy in a product.
 *
 * ## Why this exists
 *
 * Neither site mentioned the other existed. A portfolio of products on one identity plane is a
 * bigger company than either page implies, and the cost of saying so is a footer row — where the
 * cost of NOT saying so is that every visitor evaluates each product as a weekend project by
 * somebody who may not be there next year.
 *
 * It is also load-bearing rather than decorative for one of them: agitome issues no credentials
 * of its own, so a visitor persuaded by agitome.com needs to know authoxi exists before they can
 * have an account at all. That was previously discoverable only from the sign-in screen.
 *
 * ## What is NOT here
 *
 * A product that does not exist yet. `agicite` and `agentparle` are named in the roadmap and have
 * no repository; listing them would be a link to a domain that does not resolve, which is the one
 * thing a "trust us" footer must never contain.
 *
 * `LIVE` is what ships. Everything else is a roadmap entry and belongs in a roadmap.
 */

export interface PortfolioProduct {
  /** The key a product uses to exclude itself from its own footer. */
  id: string;
  name: string;
  href: string;
  /** One clause. It sits in a footer row, not on a pricing page. */
  what: string;
}

/**
 * The products with a deployed, reachable site. Ordered by where a reader meets them in the
 * story — prove who, then record what, then score it — rather than alphabetically, because the
 * order IS the explanation.
 */
export const LIVE: readonly PortfolioProduct[] = [
  {
    id: "authoxi",
    name: "authoxi",
    href: "https://authoxi.com",
    what: "identity, permissions and spend control for AI agents",
  },
  {
    id: "agitome",
    name: "agitome",
    href: "https://agitome.com",
    what: "what has actually gone wrong with an agent, and with what",
  },
] as const;

/** The one line that says how they fit, for the product asking. */
export const PORTFOLIO_LINE = "One identity plane, and the record that runs on it.";

/** Every product except the one asking. */
export function others(self: string): readonly PortfolioProduct[] {
  return LIVE.filter((p) => p.id !== self);
}
